import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { REIProgressSpinner } from "../../components/ui";
import { IZuoraConfig } from "../../interface/pricing";
import { httpPost } from "../../services/httpService";
import { ApiEndPoints } from "../../utils/endpoints";
import { getPlanConfiguration } from "../../config/pricingPlans";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_PWD, ZOURA_PWD, ZOURA_USER } from "../../utils/constants";
import Footer from "../../components/layout/footer";

interface ZuoraDigitalSignature {
  tenantId: string;
  token: string;
  signature: string;
  key: string;
}

interface ZuoraResponse {
  pageId: string;
  DigitalSignature: ZuoraDigitalSignature;
}

declare global {
  interface Window {
    Z: any;
    _hsq: any[];
    PostAffTracker: any;
  }
}

const PaymentStep: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const zuoraContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);

  const { zuoraConfig, selectedPlan, contactDetails, affiliate } = location.state || {};
  const planConfig = selectedPlan
    ? getPlanConfiguration(selectedPlan.planId, selectedPlan.billingCycle, selectedPlan.couponCode)
    : null;

  let toast: any = useRef(null);

  type CheckoutCountry = "AU" | "NZ";

  const getCountryFromUrl = (): CheckoutCountry => {
    const hostname = window.location.hostname.toLowerCase();
    return hostname.endsWith(".com.au") ? "AU" : "NZ";
  };
  type PlanId = "starter" | "professional" | "advanced";

  const getSaasCheckoutProduct = (planId: PlanId, countryCode: CheckoutCountry, productCode: string): string => {
    if (planId === "advanced" && productCode === "PRORPA1Y") {
      return countryCode === "AU"
        ? "Pro Membership AU RP Data Archistar 1 Year Upfront"
        : "Pro Membership NZ RP Data Archistar 1 Year Upfront";
    }

    const saasCheckoutProductMap: Record<PlanId, Record<CheckoutCountry, string>> = {
      starter: {
        AU: "Pro Membership AU",
        NZ: "Pro Membership NZ",
      },
      professional: {
        AU: "Pro Membership AU RP Data",
        NZ: "Pro Membership NZ RPNZ",
      },
      advanced: {
        AU: "Pro Membership AU RP Data Archistar",
        NZ: "Pro Membership NZ RP Data Archistar",
      },
    };

    return saasCheckoutProductMap[planId][countryCode];
  };

  useEffect(() => {
    if (!zuoraConfig) {
      navigate("/pricing");
      return;
    }

    initializeZuora();
  }, []);

  const initializeZuora = async (isRefresh: boolean = false) => {
    try {
      await loadZuoraSDK();

      const prepopulateFields = {
        creditCardHolderName: `${contactDetails.firstName} ${contactDetails.lastName}`,
        email: contactDetails.email,
        phone: contactDetails.mobile,
      };
      window.Z.renderWithErrorHandler(zuoraConfig, prepopulateFields, handlePaymentSuccess, handleErrorMessage);

      if (!isRefresh) {
        createHubspotContact(contactDetails.email);
      }

      if (isRefresh && zuoraContainerRef.current) {
        zuoraContainerRef.current.innerHTML = "";
      }

      setIsLoading(false);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Initialization Error",
        detail: (error as any)?.message || "Failed to load payment form. Please try again.",
      });
      setIsLoading(false);
    }
  };

  const refreshZuoraToken = async (): Promise<IZuoraConfig> => {
    const formData = {
      Firstname: contactDetails.firstName,
      Lastname: contactDetails.lastName,
      Mobile: contactDetails.mobile,
      Email: contactDetails.email,
      EmailConfirm: contactDetails.email,
      expect_to_buy: "5",
      biggest_challenge: "8",
      terms: "on",
      Coupon: selectedPlan.couponCode || "",
      Country: "AU",
      ProductCode: selectedPlan.pmId,
      Affiliate: affiliate || "",
    };

    const response = await httpPost(`${ApiEndPoints.changeEmail}/ZSignature`, formData);

    if (response.error || !response.data) {
      throw new Error(response.error || "Failed to refresh token");
    }

    const zuoraData = response.data as unknown as ZuoraResponse;
    if (!zuoraData?.DigitalSignature || !zuoraData.pageId) {
      throw new Error("Failed to refresh token");
    }
    const newZuoraConfig: any = {
      hostedPageId: zuoraData.pageId,
      tenantId: zuoraData.DigitalSignature.tenantId,
      token: zuoraData.DigitalSignature.token,
      signature: zuoraData.DigitalSignature.signature,
      key: zuoraData.DigitalSignature.key,
      apiUrl: ApiEndPoints.paymentPageUrl,
    };

    return newZuoraConfig;
  };

  const createHubspotContact = (email: string) => {
    try {
      window._hsq = window._hsq || [];
      window._hsq.push(["identify", { email }]);
      window._hsq.push(["trackPageView"]);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.message || "Failed to register contact in HubSpot",
      });
    }
  };

  const fetchDigitalSignature = async (): Promise<IZuoraConfig> => {
    try {
      const response = await axios.post(
        `${ApiEndPoints.rsaSignature}`,
        {
          username: process.env.ZOURA_USER,
          password: process.env.ZOURA_PWD,
          baseUrl: ApiEndPoints.zouraURL,
          pageId: process.env.ZOURA_PAGE_ID,
          uri: ApiEndPoints.paymentPageUrl,
        },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            "X-API-Key": API_PWD,
          },
        }
      );

      const zuoraData = response.data;
      if (!zuoraData?.digitalSignature || !zuoraData?.pageId) {
        throw new Error("Failed to fetch digital signature");
      }
      let zuoraConfig: IZuoraConfig = {
        hostedPageId: zuoraData.pageId,
        tenantId: zuoraData.digitalSignature.tenantId,
        token: zuoraData.digitalSignature.token,
        signature: zuoraData.digitalSignature.signature,
        apiUrl: ApiEndPoints.paymentPageUrl,
        key: zuoraData.digitalSignature.key,
        accountId: "",
      };
      return zuoraConfig;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || "Failed to fetch digital signature");
    }
  };

  const registerAffiliateTracking = (email: string) => {
    try {
      if (window.PostAffTracker) {
        const sale = window.PostAffTracker.createSale();
        sale.setTotalCost("0.00");
        sale.setOrderID(email);
        sale.setData1(email);
        sale.setProductID("PB");
        window.PostAffTracker.register();
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.message || "Failed to register affiliate tracking",
      });
    }
  };

  const updateHubspotContact = async (email: string, productCode: string, couponCode: string) => {
    try {
      const fallbackCheckoutType = ["PRO1", "PRORP1", "PRORPA1"].includes(productCode) ? "Casual" : "Upfront";
      const subscriptionCountryCode = getCountryFromUrl() || "AU";

      const data = {
        email: email,
        saasCheckoutProduct: getSaasCheckoutProduct(
          selectedPlan.planId as PlanId,
          subscriptionCountryCode as CheckoutCountry,
          productCode
        ),
        saasCheckoutType: fallbackCheckoutType,
        zuoraSubscriptionStatus: "Active",
      };
      await httpPost(`${ApiEndPoints.signUp}/UpdateSignUpDetails`, data).catch((err) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: err.message || "Failed to update contact details in HubSpot",
        });
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.message || "Failed to update contact details in HubSpot",
      });
    }
  };

  const updateAffiliateCode = async (accountId: string) => {
    try {
      await axios.put(
        `${ApiEndPoints.zouraExternalURL}/${accountId}`,
        {
          baseUrl: ApiEndPoints.zouraURL,
          apiSecretAccessKey: ZOURA_PWD,
          apiAccessKeyId: ZOURA_USER,
          accountId,
          updateAccount: {
            affiliateCode__c: affiliate || "",
          },
        },
        {
          headers: {
            "X-API-Key": API_PWD,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.message || "Failed to update affiliate code in Zuora",
      });
    }
  };

  const handleErrorMessage = (key: string, code: string, message: string, rawGatewayInfo?: string) => {
    let errorMessage = message;

    if (key !== "error") {
      switch (key) {
        case "creditCardNumber":
          if (code === "001") {
            errorMessage = "Card number required. Please input the card number first.";
          } else if (code === "002") {
            errorMessage = "Number does not match credit card. Please try again.";
          }
          break;
        case "cardSecurityCode":
        case "creditCardExpirationYear":
        case "creditCardExpirationMonth":
        case "creditCardType":
          break;
      }
    } else {
      if (code === "unknown" && rawGatewayInfo) {
        errorMessage = rawGatewayInfo;
      } else {
        if (message === "[Attempt_Exceed_Limitation] Attempt exceed the limitation, refresh page to try again.") {
          errorMessage = "Page refreshed due to access token overuse.";
          initializeZuora(true);
        } else {
          errorMessage = `Validation failed: ${message}`;
        }
      }
    }

    window.Z.sendErrorMessageToHpm(key, errorMessage);
  };

  const loadZuoraSDK = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.Z) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://static.zuora.com/Resources/libs/hosted/1.3.1/zuora-min.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Zuora SDK"));
      document.head.appendChild(script);
    });
  };

  const handlePaymentSuccess = async (response: any) => {
    if (!response.success) {
      toast.current?.show({
        severity: "error",
        summary: "Payment Error",
        detail: `Error: ${response.errorCode} - ${response.errorMessage}`,
      });
      return;
    }

    setIsSubscribing(true);

    try {
      const payload = {
        Email: contactDetails.email,
        Firstname: contactDetails.firstName,
        Lastname: contactDetails.lastName,
        Mobile: contactDetails.mobile,
        PaymentId: response.refId,
        ProductCode: selectedPlan.pmId,
        SubscriptionCountryCode: "AU",
        Coupon: selectedPlan.couponCode,
      };

      await httpPost(`${ApiEndPoints.changeEmail}/subscribe`, payload)
        .then(async (res) => {
          if (res.status !== 200 || !res.data?.success) {
            throw new Error("Subscription creation failed");
          }
          await updateHubspotContact(contactDetails.email, selectedPlan.pmId, selectedPlan.couponCode).then(() => {
            if (res.data?.accountId != null && res.data?.accountId != "" && affiliate != null && affiliate != "") {
              updateAffiliateCode(res.data.accountId);
            }
          });
          navigate("/signup/success", { state: { plan: selectedPlan.planName } });
        })
        .catch((err) => {
          if (err) {
            throw new Error(err.message || "Failed to create subscription");
          }
        });
    } catch (error: any) {
      const errorMsg = `We encountered an error processing the payment.\n\nThis is the error message from the payment processor;\n\n${error.message}\n\nPlease try again noting the above error.\n\nPlease note due to PCI compliance rules and the fact your payment data is sent directly to Zuora you will have to fill out some information again, sorry for any inconvenience.\n\nIf you continue to receive problems signing up please contact our customer success team during business hours on +61(7) 5655 7588 or info@realestateinvestar.com.au`;
      alert(errorMsg);
      setIsSubscribing(false);
      initializeZuora(true);
    }
  };

  if (!zuoraConfig) {
    return null;
  }

  return (
    <>
      <div className="payment-step min-h-screen bg-gray-50 py-12">
        <Toast ref={toast} position="top-right" />
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{selectedPlan.planName} Membership Sign Up</h1>
            <p className="text-xl text-gray-600">Step 2 of 2: Payment Details</p>
          </div>

          <Card>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">{selectedPlan.planName} Plan</h3>
                  <p className="text-gray-600">
                    {selectedPlan.billingCycle === "annual" ? "Annual" : "Monthly"} Billing
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">${selectedPlan.displayPrice}</div>
                  <div className="text-sm text-gray-600">
                    {selectedPlan.billingCycle === "annual" ? "per year" : "per month"}
                  </div>
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="text-center py-12">
                <REIProgressSpinner />
                <p className="mt-4 text-gray-600">Loading secure payment form...</p>
              </div>
            )}

            <div ref={zuoraContainerRef} id="zuora_payment" className={isLoading ? "hidden" : ""} />

            {isSubscribing && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-lg shadow-xl text-center">
                  <REIProgressSpinner />
                  <p className="mt-4 text-gray-700 font-semibold">Processing your subscription...</p>
                  <p className="text-sm text-gray-500 mt-2">Please do not close this window</p>
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <img src="/assets/ssl-secure.png" alt="SSL Secure" className="h-8 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Your payment is processed securely through Zuora</p>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentStep;
